import { useTranslation } from 'react-i18next'

import type { StateCategory } from '@/types/domain'
import type { RollupDivision } from '@/types/planning'

const BAR: { category: StateCategory; color: string }[] = [
  { category: 'started', color: 'var(--color-state-started)' },
  { category: 'blocked', color: 'var(--color-state-blocked)' },
  { category: 'backlog', color: 'var(--color-state-backlog)' },
  { category: 'done', color: 'var(--color-state-done)' },
  { category: 'cancelled', color: 'var(--color-state-cancelled)' },
]

/**
 * All five divisions rolled into one view.
 *
 * The bar is built from the normalised state categories, not from stage names —
 * which is the only reason five divisions running five different lifecycles can
 * be shown side by side at all.
 */
export function DivisionRollup({ divisions }: { divisions: RollupDivision[] }) {
  const { t } = useTranslation()

  return (
    <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {divisions.map((division) => {
        const total = Math.max(1, division.projects)

        return (
          <li key={division.id ?? division.key} className="border-line bg-surface rounded-md border p-4">
            <div className="flex items-baseline gap-2">
              <span className="text-ink-500 font-mono text-[10px] tracking-widest uppercase">
                {division.key}
              </span>
              {division.stalled > 0 ? (
                <span className="ms-auto rounded border border-[color:var(--color-state-blocked)] px-1.5 py-0.5 font-mono text-[10px] text-[color:var(--color-state-blocked)]">
                  {division.stalled} {t('portfolio.stalled').toLowerCase()}
                </span>
              ) : null}
            </div>

            <div className="text-ink-900 mt-1 font-medium">{division.name}</div>

            <div className="text-ink-700 mt-2 flex items-baseline gap-3 text-sm tabular-nums">
              <span>
                {division.projects} {t('portfolio.projects').toLowerCase()}
              </span>
              <span className="text-ink-500">
                {division.openWorkItems} {t('portfolio.openItems').toLowerCase()}
              </span>
            </div>

            <div
              className="bg-surface-2 mt-3 flex h-2 overflow-hidden rounded-full"
              aria-hidden="true"
            >
              {BAR.map((segment) => {
                const count = division.byCategory[segment.category] ?? 0
                if (count === 0) return null

                return (
                  <span
                    key={segment.category}
                    style={{ width: `${(count / total) * 100}%`, backgroundColor: segment.color }}
                  />
                )
              })}
            </div>

            <ul className="text-ink-500 mt-2 flex flex-wrap gap-x-3 gap-y-1 font-mono text-[10px]">
              {BAR.map((segment) => {
                const count = division.byCategory[segment.category] ?? 0
                if (count === 0) return null

                return (
                  <li key={segment.category} className="flex items-center gap-1">
                    <span
                      aria-hidden="true"
                      className="size-1.5 rounded-full"
                      style={{ backgroundColor: segment.color }}
                    />
                    {t(`state.${segment.category}`)} {count}
                  </li>
                )
              })}

              {/* An empty bar with no legend reads as a rendering fault. It is
                  not: these projects have no stage recorded, which is itself
                  the thing to notice. */}
              {division.byCategory.unset === 0 ? null : (
                <li className="flex items-center gap-1">
                  <span aria-hidden="true" className="border-line size-1.5 rounded-full border" />
                  {t('portfolio.noStage')} {division.byCategory.unset}
                </li>
              )}
            </ul>
          </li>
        )
      })}
    </ul>
  )
}
